import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@lib/i18n';
import styles from './index.module.scss';
import { TRANSLATIONS_ENABLED, LANGUAGES_JSON } from '@lib/constants';
import { HP_ANIMATIONS, HP_ANIMATION_TYPE } from '@lib/constants';
import { staggerAnimations } from '@lib/helpers';
import { initTriggeredAnimations } from '@lib/helpers/blockAnimations';

export default function LanguageSelector({ className = '' }) {
	const { locale, setLocale, translations_ready } = useI18n();

	const [mounted, setMounted] = useState(false);
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef(null);

	const animationClass =
		HP_ANIMATIONS && HP_ANIMATION_TYPE ? `animate ${HP_ANIMATION_TYPE} hp-has-triggered-animation` : '';

	const animationData =
		HP_ANIMATIONS && HP_ANIMATION_TYPE
			? {
					'data-hp-trigger-selector': '',
					'data-hp-triggered-animation': HP_ANIMATION_TYPE,
					'data-hp-trigger-event': 'viewport',
					'data-hp-triggered-played': '0',
				}
			: {};

	useEffect(() => {
		setMounted(true);
	}, []);

	useEffect(() => {
		if (mounted && translations_ready) {
			staggerAnimations();
			initTriggeredAnimations();
		}
	}, [mounted, translations_ready]);

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setOpen(false);
			}
		};

		const handleEscape = (event) => {
			if (event.key === 'Escape') {
				setOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		document.addEventListener('keydown', handleEscape);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('keydown', handleEscape);
		};
	}, []);

	if (!TRANSLATIONS_ENABLED) {
		return null;
	}

	if (!mounted || !translations_ready) {
		return null;
	}

	const currentLanguage = LANGUAGES_JSON.find(({ key }) => key === locale) || LANGUAGES_JSON[0];

	const handleSelect = (value) => {
		setLocale(value);
		setOpen(false);
	};

	return (
		<div className={`${styles.wrapper} ${className}`} ref={wrapperRef}>
			<button
				type="button"
				className={`${styles.trigger} ${open ? styles.open : ''} ${animationClass}`}
				onClick={() => setOpen((prev) => !prev)}
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-labelledby="language-selector-label"
				{...animationData}
			>
				<span className={styles.triggerLabel} id="language-selector-label">
					{currentLanguage.label}
				</span>
				<span className={styles.chevron} aria-hidden="true" />
			</button>

			{open && (
				<ul className={styles.menu} role="listbox" aria-label="Select language">
					{LANGUAGES_JSON.map(({ key, label }) => {
						const isSelected = key === locale;

						return (
							<li key={key} role="option" aria-selected={isSelected}>
								<button
									type="button"
									className={`${styles.option} ${isSelected ? styles.selected : ''}`}
									onClick={() => handleSelect(key)}
								>
									{label}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
}
